import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import DirectionsRunOutlinedIcon from "@mui/icons-material/DirectionsRunOutlined";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import PiChipSelect, { PiChipSelectOption } from "../../components/PiChipSelect";
import { ActorMovementType } from "../../models/ActorMovementType";
import { ActorState } from "../../models/ActorState";

const icon = (Icon: typeof BoltOutlinedIcon) => <Icon className="flex-none" sx={{ fontSize: 18 }} />;

/**
 * Movement as a chip dropdown, like the other rows of the take actor settings.
 * NPCs get Instant and Walk; the player also gets Auto walk and Find. Each
 * option explains itself in the menu instead of in a hover tooltip.
 */
export default function ActorStateMovementSelect(props: {
  actor: ActorState;
  onChange: (movement: ActorMovementType) => void;
  disabled: boolean;
  /** Why movement can't change (e.g. the actor's first appearance); shown on hover and focus. */
  disabledReason?: string;
}) {
  const { actor, onChange, disabled, disabledReason } = props;
  const { t } = useTranslation();

  const options: PiChipSelectOption[] = [
    {
      id: String(ActorMovementType.Instant),
      label: String(t("instant")),
      icon: icon(BoltOutlinedIcon),
      description: String(t("actor_settings.movement_instant_hint")),
    },
    {
      id: String(ActorMovementType.Walk),
      label: String(t("walk")),
      icon: icon(DirectionsRunOutlinedIcon),
      description: String(t("actor_settings.movement_walk_hint")),
    },
  ];
  if (actor.actorId === "player") {
    options.push(
      {
        id: String(ActorMovementType.AutoWalk),
        label: String(t("auto_walk")),
        icon: icon(PlayArrowOutlinedIcon),
        description: String(t("auto_walk_tooltip")),
      },
      {
        id: String(ActorMovementType.Explore),
        label: String(t("explore")),
        icon: icon(ExploreOutlinedIcon),
        description: String(t("explore_tooltip")),
      }
    );
  }

  const select = (
    <PiChipSelect
      aria-label={String(t("actor_settings.movement"))}
      tone="setting"
      value={String(actor.movement)}
      options={options}
      onChange={(id) => onChange(Number(id) as ActorMovementType)}
      placeholder={String(t("instant"))}
      disabled={disabled}
    />
  );

  if (!disabledReason) return select;

  // A disabled button gets no hover or focus, so the reason sits on a focusable wrapper.
  return (
    <Tooltip title={disabledReason} arrow describeChild placement="top">
      <div
        className="inline-flex"
        role="group"
        aria-label={String(t("actor_settings.movement_unavailable"))}
        tabIndex={0}
        data-testid="movement-select-tooltip-target"
      >
        {select}
      </div>
    </Tooltip>
  );
}
